<ng-template #rechargeModal let-modal>
  <div class="recharge-modal">
    <div class="modal-header">
      <div class="recharge-modal__head">
        <span class="recharge-modal__badge"><i class="fa fa-money"></i></span>
        <div>
          <h4 class="modal-title">Adicionar saldo</h4>
          <p class="recharge-modal__subtitle">Saldo em R$ para os seus envios e a IA</p>
        </div>
      </div>
      <button type="button" class="btn-close" aria-label="Fechar" (click)="modal.dismiss('CLOSE')">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      <!-- Passo 1: escolher o valor -->
      <ng-container *ngIf="step === 'amount'">
        <label class="recharge-modal__label" for="recharge-amount">
          Selecione ou digite o valor da recarga (R$)
        </label>

        <!-- Os chips são as faixas de bônus, não valores bonitos escolhidos
             aqui: cada um mostra o piso que destrava a faixa e o que ela dá. -->
        <div class="tier-chips" *ngIf="tiers.length > 0" data-testid="tier-chips">
          <button type="button" class="tier-chip" *ngFor="let tier of tiers"
            [class.tier-chip--active]="isTierSelected(tier)" (click)="selectTier(tier)"
            data-testid="tier-chip">
            <!-- Duas casas mesmo em valor redondo: o piso do `MoneyMicrosPipe`
                 é o centavo, e afrouxá-lo aqui abriria exceção de escala numa
                 tela de dinheiro para ganhar quatro caracteres. -->
            <strong>{{ tier.min_total_micros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}</strong>
            <!-- Conta excluída do bônus: o chip vira só atalho de valor, senão
                 prometeria um bônus que o resumo depois mostraria zerado. -->
            <small *ngIf="!accountBonusExclusion">+{{ tier.bonus_micros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }} de bônus</small>
          </button>
        </div>

        <p class="quote-summary__reason recharge-modal__notice" *ngIf="showAccountBonusExclusion"
          data-testid="recharge-bonus-exclusion">
          <i class="fa fa-info-circle"></i> {{ accountBonusExclusion }}
        </p>

        <div class="amount-field">
          <span class="amount-field__prefix">R$</span>
          <input id="recharge-amount" type="number" class="form-control" inputmode="decimal" step="0.01" min="0"
            placeholder="Outro valor..." [(ngModel)]="amount" (ngModelChange)="onAmountChanged()"
            data-testid="recharge-amount" />
        </div>

        <p class="amount-error" *ngIf="amountTooManyDecimals" data-testid="recharge-decimals-error">
          <i class="fa fa-exclamation-circle"></i>
          Use no máximo 2 casas decimais (centavos).
        </p>

        <p class="amount-error" *ngIf="isBelowMinimum" data-testid="recharge-minimum-error">
          <i class="fa fa-exclamation-circle"></i>
          A recarga mínima é de {{ minimumLabelMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}.
        </p>

        <!-- Resumo ao vivo: o que sai do bolso, o que a faixa dá e o que entra
             na carteira. Os três juntos, porque é a soma que o cliente compara
             com o valor que digitou. -->
        <div class="quote-summary" *ngIf="hasAmount && !isBelowMinimum" data-testid="recharge-quote">
          <span class="quote-summary__loading" *ngIf="quoting" data-testid="recharge-quote-loading">
            <i class="fa fa-circle-o-notch fa-spin"></i> {{ bonusEligible ? 'Calculando o bônus…' : 'Calculando…' }}
          </span>

          <ng-container *ngIf="!quoting">
            <div class="quote-summary__line">
              <span>Você paga</span>
              <strong data-testid="recharge-paid">
                {{ amountMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
              </strong>
            </div>
            <!-- Conta que não recebe bônus (CU-17tm466tzkz): sem a linha. -->
            <div class="quote-summary__line" *ngIf="bonusEligible" data-testid="recharge-bonus-line">
              <span>Bônus</span>
              <strong class="quote-summary__bonus" data-testid="recharge-bonus">
                {{ bonusMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
              </strong>
            </div>
            <div class="quote-summary__line quote-summary__line--total">
              <span>Saldo creditado</span>
              <strong data-testid="recharge-credited">
                {{ creditedMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
              </strong>
            </div>
          </ng-container>

          <p class="quote-summary__reason" *ngIf="exclusionReason" data-testid="recharge-quote-reason">
            <i class="fa fa-info-circle"></i> {{ exclusionReason }}
          </p>

          <p class="quote-summary__reason" *ngIf="quoteFailed && bonusEligible" data-testid="recharge-quote-failed">
            <i class="fa fa-exclamation-triangle"></i>
            Não foi possível calcular o bônus agora — o valor pago é creditado do mesmo jeito.
          </p>
        </div>

        <!-- Quanto rende (CU-86ak77za5, 10/10/2026): preço DESTE cliente de cada produto
             cobrado pela carteira (SMS, WhatsApp, ligação) e o que o saldo projetado (hoje +
             recarga + bônus) paga em cada um, mais a linha de IA. Os números já vêm prontos
             de `yieldRows`/`aiCostRange` (recharge-yield.ts) — este bloco só desenha; nenhuma
             conta é feita aqui. -->
        <div class="recharge-yield sf-card" *ngIf="hasYieldTable" data-testid="recharge-yield">
          <p class="section-title">
            <i class="fa fa-line-chart"></i> Quanto o seu saldo rende
          </p>

          <div class="recharge-yield__balances" *ngIf="availableMicros !== null">
            <div class="recharge-yield__balance-line">
              <span>Saldo atual da carteira</span>
              <strong data-testid="recharge-current-balance">
                {{ availableMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
              </strong>
            </div>
            <div class="recharge-yield__balance-line" *ngIf="hasAmount && !isBelowMinimum">
              <span>Saldo após a recarga</span>
              <strong data-testid="recharge-projected-balance">
                {{ projectedBalanceMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
              </strong>
            </div>
          </div>

          <div class="recharge-yield__table-wrap">
            <table class="table recharge-yield__table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Seu preço</th>
                  <th>Quanto rende com saldo + recarga</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of yieldRows; trackBy: trackYieldRow" [attr.data-testid]="'recharge-yield-row-' + row.service">
                  <td>
                    <span class="recharge-yield__product">
                      <i class="fa service-icon" [ngClass]="'service-icon--' + row.service"
                        [class.fa-commenting]="row.service === 'sms'"
                        [class.fa-whatsapp]="row.service === 'whatsapp'"
                        [class.fa-phone]="row.service === 'call'"></i>
                      {{ row.label }}
                    </span>
                  </td>

                  <td data-label="Seu preço" [attr.data-testid]="'recharge-yield-price-' + row.service">
                    <ng-container [ngSwitch]="row.status">
                      <ng-container *ngSwitchCase="'priced'">
                        {{ row.unitPriceMicros | moneyMicros : 'BRL' : balanceLocale }} {{ row.unitSuffix }}
                        <small>({{ row.sourceLabel }})</small>
                      </ng-container>
                      <ng-container *ngSwitchCase="'credits'">Pacote de créditos</ng-container>
                      <ng-container *ngSwitchDefault>Sem preço definido</ng-container>
                    </ng-container>
                  </td>

                  <td data-label="Quanto rende" [attr.data-testid]="'recharge-yield-quantity-' + row.service">
                    <ng-container [ngSwitch]="row.status">
                      <ng-container *ngSwitchCase="'priced'">
                        <ng-container *ngIf="row.quantity !== null; else noQuantity">
                          ≈ {{ formatQuantity(row.quantity) }} {{ row.quantityUnitLabel }}
                          <small *ngIf="row.callMinutesUpTo !== null">(até {{ formatQuantity(row.callMinutesUpTo) }} min)</small>
                        </ng-container>
                        <ng-template #noQuantity>—</ng-template>
                      </ng-container>
                      <ng-container *ngSwitchCase="'credits'">Não sai deste saldo</ng-container>
                      <ng-container *ngSwitchDefault>Envio bloqueado</ng-container>
                    </ng-container>
                  </td>
                </tr>

                <!-- IA não é produto da carteira (`rechargeYieldRows` não a inclui — o preço
                     dela não é por unidade), por isso a linha é fixa e não entra no *ngFor. -->
                <tr data-testid="recharge-yield-row-ai">
                  <td>
                    <span class="recharge-yield__product">
                      <i class="fa fa-magic service-icon service-icon--ai"></i>
                      IA
                    </span>
                  </td>
                  <td data-label="Seu preço" data-testid="recharge-yield-price-ai">
                    <ng-container *ngIf="aiBilledByWallet; else aiCredits">
                      Por uso, varia com o modelo
                      <small *ngIf="aiCostRange" data-testid="recharge-ai-range">
                        — suas últimas {{ formatQuantity(aiCostRange.sampleSize) }} respostas ({{ aiCostRange.windowDays }} dias)
                        custaram, em geral, entre
                        {{ aiCostRange.lowMicros | moneyMicros : 'BRL' : balanceLocale : aiCostMaxFractionDigits }}
                        e
                        {{ aiCostRange.highMicros | moneyMicros : 'BRL' : balanceLocale : aiCostMaxFractionDigits }}
                        cada
                      </small>
                    </ng-container>
                    <ng-template #aiCredits>Pacote de créditos</ng-template>
                  </td>
                  <td data-label="Quanto rende" data-testid="recharge-yield-quantity-ai">
                    {{ aiBilledByWallet ? 'Depende do uso' : 'Não sai deste saldo' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Uma forma só, e visível: esconder o bloco faria o cliente chegar ao
             botão sem saber como vai pagar. -->
        <p class="recharge-modal__label">Forma de pagamento</p>
        <div class="payment-options">
          <button type="button" class="payment-option payment-option--active" data-testid="payment-pix">
            <i class="fa fa-qrcode"></i>
            <span>PIX</span>
          </button>
        </div>
      </ng-container>

      <!-- Passo 2: o PIX gerado, no mesmo desenho da compra por quantidade. -->
      <div class="pix-panel" *ngIf="step === 'pix'" data-testid="recharge-pix">
        <p class="pix-panel__hint">Escaneie o QR Code no aplicativo do seu banco.</p>
        <img *ngIf="pixImage" [src]="pixImage" class="pix-panel__image" alt="QR Code do PIX" />
        <div *ngIf="pixCode" class="pix-panel__code">
          <p>
            <small>Se não conseguir ler o QR Code, use o código abaixo no "PIX Copia e Cola":</small>
          </p>
          <textarea rows="3" class="form-control text-center" readonly>{{ pixCode }}</textarea>
          <button type="button" class="sf-btn sf-btn--secondary sf-btn--sm" (click)="pixCopy()"
            data-testid="recharge-pix-copy">
            <i class="fa fa-copy"></i>
            Copiar
          </button>
        </div>
        <a class="pix-panel__link" [routerLink]="['/recharges']" (click)="modal.dismiss('CLOSE')">
          Ver minhas recargas
        </a>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="sf-btn sf-btn--secondary" (click)="modal.dismiss('CLOSE')"
        data-testid="recharge-close">
        Fechar
      </button>
      <!-- Largura total: é a confirmação do modal, e no protótipo ela ocupa o
           rodapé inteiro em vez de disputar a linha com o `Fechar`. -->
      <button type="button" class="sf-btn sf-btn--primary sf-btn--lg sf-btn--block" *ngIf="step === 'amount'"
        [disabled]="!canBuy" [appLoadingButton]="buying" (click)="buy()" data-testid="recharge-submit">
        Gerar PIX de
        {{ amountMicros | moneyMicros : 'BRL' : balanceLocale : balanceFractionDigits : balanceFractionDigits }}
      </button>
    </div>
  </div>
</ng-template>
